import { useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DollarSign, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { Card, CardHead, SectionHead, Mantra, MantraAccent } from "@/components/brand/Card";
import { Legend } from "@/components/brand/Toolbar";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ErrorState";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useProject,
  useProjectCosts,
  useProjectRuns,
  useWorkspaceSettings,
} from "@/hooks/useApi";
import { compactUsd, compactTokens, detailedTokens, detailedUsd } from "@/lib/format";
import { formatDateTime, formatDayShort } from "@/lib/datetime";
import { AGENT_DISPLAY_NAMES } from "@/lib/agent-ids";
import { runModelLabel } from "@/lib/model-label";
import { tp } from "@/lib/plural";
import { costCsvFilename, downloadCostCsv } from "@/lib/cost-csv";
import type { CostByDay, CostByModel } from "@/types/api";
import type { ProjectRun } from "@/api/agents";

/**
 * Cost & Tokens — #429 (SIMP.8).
 *
 * Até 16/09/2026 esta tela era 100% mock: três arrays fixos (`AGENTS`, `DAILY`,
 * `INVOICE`) e uma dúzia de números cravados — rótulos de dia parados em abril
 * num app cujo hoje é setembro, um mix por modelo com porcentagens que ninguém
 * mediu, e um teto de orçamento sem nenhuma relação com `budget_cap_usd`. O
 * dado real já existia: `useProjectCosts` é o MESMO hook que o Overview lê, e
 * por isso os dois batem por construção — nenhuma soma paralela.
 *
 * (Os literais do mock estão descritos, não citados, de propósito: a suíte
 * `project-cost-reais.test.tsx` varre este arquivo atrás deles, e citá-los na
 * prosa obrigaria a afrouxar a própria trava que impede o retorno deles.)
 *
 * Saíram sem substituto (nenhuma fonte no backend): tabela de runs recentes
 * (a casa canônica de histórico de execução é `/runs`, hoje lendo dado real —
 * #867), forecast (calculável, mas é
 * estimativa — decisão de não misturar com dado medido), billing cycle,
 * plano/tier, pricing por token, cache hit, avg latency (real em
 * `metrics.latency_ms`, mas sem agregação no `agents` — fica para outro
 * ticket) e os botões Budget alerts/Invoice preview (nenhum tinha handler
 * nem fonte). O CSV voltou: os três recortes já chegam ao cliente na mesma
 * chamada que desenha a tela, então exportá-los não pede rota nova
 * (`lib/cost-csv.ts`).
 *
 * **O split input/output entrou depois (#867).** Esta prosa dizia que ele
 * ficava de fora porque "o executor nunca grava
 * `prompt_tokens`/`completion_tokens`" — verdade em 16/09/2026, falsa desde
 * que o #867 fez o executor gravá-los onde o grafo mede e o `get_cost_summary`
 * somá-los por dia. Ele aparece **escopado**, nunca empilhado sob o total:
 * veja o porquê no `DailyChart`.
 *
 * Cores dos gráficos: a paleta do mock antigo (`#08254c`, `#596b85` como
 * preenchimento de série) reprova o validador da skill `dataviz` — a primeira
 * sai da faixa de luminância/croma de marca categórica (é cor de texto/heading
 * da marca, não de dado), a segunda lê como cinza. Uso o tema categórico
 * padrão validado da skill só para as marcas de dado; o resto da página
 * continua no brand kit existente.
 */

/** Tema categórico validado (skill dataviz, `references/palette.md`) — só
 * para preenchimento de marca de dado, nunca para texto. */
const CHART_BLUE = "#2a78d6";
/** Slots 1-3, para a composição "By model" (tipicamente 1-3 modelos por
 * projeto — troca de modelo em migração longa é o caso comum, não a exceção;
 * ver `test_cost_summary_models.py`). 4º+ dobra em "Other". */
const MODEL_SLOTS = ["#2a78d6", "#eb6834", "#1baf7a"] as const;
/** Neutro para o resto ("Other") — reaproveita o cinza estrutural que
 * `ProjectRuns.tsx` já usa para estágio inativo, não uma cor nova. Também
 * serve ao segmento "split não gravado" da pilha diária: nos dois casos ele
 * marca ausência de identidade, não mais uma categoria. */
const MODEL_OTHER = "#b2bccb";

/** Quantas linhas de fatura a tela mostra — e pede. */
const CHARGE_ROWS = 5;

/** Assinatura mínima de `t` que as funções puras abaixo precisam — evita
 * importar o tipo pesado de `i18next` só para uma chamada de tradução. */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Início do mês corrente em UTC — o Budget Left compara contra
 * `budget_cap_usd`, que é um teto **mensal** por definição (workspace
 * settings). O KPI de gasto acumulado usa a chamada sem filtro, a mesma que
 * o Overview lê; só o Budget Left precisa deste recorte à parte, senão um
 * projeto com meses de vida compararia gasto vitalício contra teto mensal —
 * sempre "estourado", ainda que cada mês individual estivesse dentro do cap. */
function currentMonthStartIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

export default function ProjectCost() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project } = useProject(projectId);
  // `includeDaily` só aqui: esta é a única tela que desenha a série. A
  // Overview e o workbench leem o mesmo endpoint sem o opt-in e voltam a
  // custar as quatro consultas que custavam antes da #429.
  const { data: costs, isLoading: costsLoading } = useProjectCosts(projectId, {
    includeDaily: true,
  });
  const { data: workspace } = useWorkspaceSettings();
  // As linhas de fatura são as execuções recentes que cobraram. A fonte é a
  // MESMA rota que a tela de Runs lê (`/runs`, #867) — somar custo por aqui
  // com uma consulta própria abriria a divergência que a #429 fechou entre
  // esta tela e o Overview.
  //
  // O corte "custou algo" é do banco (`has_cost`), não daqui. Antes a tela
  // pedia as 20 mais recentes e filtrava `custo > 0` no navegador, o que olhava
  // uma janela de 20 e não o projeto: com as 20 últimas a $0.00 —
  // Configuration Server e Architect sem LLM —, o card dizia "nenhuma cobrança
  // gravada" ao lado de um hero com gasto.
  const {
    data: runs,
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
  } = useProjectRuns(projectId, { hasCost: true, limit: CHARGE_ROWS });
  const cap = workspace?.budget_cap_usd ?? null;
  // O recorte do mês existe só para o Budget Left, que precisa de um escalar
  // (`total_cost`) — nunca da série. E só sai do lugar quando há cap para
  // comparar: sem cap o KPI não aparece, e disparar a consulta assim mesmo
  // seria pagar por um resultado que ninguém mostra. `cap` indefinido (as
  // settings ainda não chegaram) também segura — a consulta dispara sozinha
  // quando o valor chegar.
  const {
    data: monthCosts,
    isLoading: monthLoading,
    isError: monthError,
  } = useProjectCosts(projectId, {
    dateFrom: currentMonthStartIso(),
    enabled: cap != null,
  });

  if (costsLoading) {
    return (
      <div className="-m-6">
        <Skeleton className="h-[220px] w-full rounded-none" />
        <div className="p-8 space-y-6">
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!costs) {
    return (
      <div className="-m-6">
        <CompactHero glyph={<DollarSign />} overline={t("USAGE & BILLING")} title={t("Cost & Tokens")} />
        <div className="p-8">
          <Card>
            <p className="text-sm text-muted-foreground text-center py-12">
              {t("Cost data unavailable.")}
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const byAgent = costs.by_agent ?? [];
  const byModel = costs.by_model ?? [];
  const byDay = costs.by_day ?? [];
  // Rótulo antes do id cru, e nada quando nenhum dos dois veio — `agents`
  // antigo não manda a chave, e "Configured: undefined" é pior que silêncio.
  const embeddingModel = costs.embedding_model_label || costs.embedding_model_id || null;
  const charges = runs?.items ?? [];

  // Achado do review: `chat_count` conta MENSAGENS de chat (`COUNT(*)` sobre
  // `chat_messages`), não runs — o rótulo de cada linha de "By agent" (mais
  // abaixo) já faz essa distinção. Somar os dois aqui inflava "Total runs" e
  // diluía "Avg cost / run" por um denominador maior que o real.
  const totalRuns = byAgent.reduce((sum, a) => sum + a.execution_count, 0);
  // As duas pontas da média saem do MESMO recorte.
  //
  // O `total_cost` do resumo soma execução **+ chat** (`chat_rows`, em
  // `persistence.py`), e o denominador acima conta só execução — de propósito
  // e com razão. Dividir um pelo outro atribuía à execução um dinheiro que ela
  // não gastou, e o erro cresce com a conversa: dois runs de $1 num projeto
  // com $8 de chat liam "$5,00 por execução", cinco vezes o que qualquer
  // execução custou. A correção do denominador veio na #429; o numerador
  // ficou para trás.
  const executionCost = byAgent.reduce((sum, a) => sum + a.execution_cost, 0);
  // `null`, não zero: projeto que só conversou não tem média por execução, e
  // "$0.00 por execução" afirmaria que executar ali não custa nada.
  const avgCostPerRun = totalRuns > 0 ? executionCost / totalRuns : null;

  // `??0` some perto de "sem cap" e "ainda não sei" — a mesma sequência de
  // toda carga: `cap` responde primeiro (some do `useWorkspaceSettings`),
  // libera esta consulta (linha ~99), e por um instante `monthCosts` ainda
  // não chegou. Achado do review: cravar 100% de teto livre nessa janela
  // (ou para sempre, se a consulta falhar) é pior que não mostrar o KPI.
  const budgetKnown = cap != null && !monthLoading && !monthError && monthCosts != null;
  const monthSpend = monthCosts?.total_cost ?? 0;
  const budgetRemaining = budgetKnown ? cap - monthSpend : null;
  // Clampado em 0 para exibição — negativo (estourou o cap) vira sub-rótulo,
  // não porcentagem negativa.
  //
  // Teto **zero** é configuração válida (`budget_cap_usd` é `ge=0` no
  // backend) e significa "nenhum gasto autorizado" — o oposto de "sem teto".
  // Dividir por ele daria `Infinity`, e era por isso que o guarda antigo
  // (`cap > 0`) existia; só que ele escondia o KPI inteiro, justamente do
  // tenant que mais precisa do aviso de estouro. Não há fração de zero: o
  // restante é 0% por definição, e o sub-rótulo diz quanto passou.
  const budgetPct = !budgetKnown
    ? null
    : cap > 0
      ? Math.max(0, Math.round(((budgetRemaining ?? 0) / cap) * 100))
      : 0;

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<DollarSign />}
        overline={`${(project?.name || "PROJECT").toUpperCase()} · ${t("USAGE & BILLING")}`}
        title={t("Cost & Tokens")}
        actions={
          <Button
            variant="ghost-light"
            size="sm"
            className="h-9 gap-2 px-3.5 text-xs"
            onClick={() =>
              downloadCostCsv(
                costs,
                costCsvFilename(project?.name, new Date().toISOString().slice(0, 10)),
                t,
              )
            }
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        }
        kpis={[
          { label: t("Total spend"), value: compactUsd(costs.total_cost), gold: true },
          { label: t("Total tokens"), value: compactTokens(costs.total_tokens) },
          {
            label: t("Avg cost / run"),
            value: avgCostPerRun != null ? compactUsd(avgCostPerRun) : "—",
            sub:
              avgCostPerRun != null
                ? tp(t, totalRuns, "across 1 run", "across {{n}} runs")
                : t("No run yet"),
          },
          { label: t("Total runs"), value: String(totalRuns) },
          ...(budgetKnown && budgetPct != null
            ? [
                {
                  label: t("Budget Left"),
                  value: budgetPct,
                  unit: "%",
                  sub:
                    (budgetRemaining ?? 0) >= 0
                      ? t("{{spent}} of {{cap}} this month", {
                          spent: compactUsd(monthSpend),
                          cap: compactUsd(cap),
                        })
                      : t("{{over}} over {{cap}} monthly cap", {
                          over: compactUsd(Math.abs(budgetRemaining ?? 0)),
                          cap: compactUsd(cap),
                        }),
                },
              ]
            : []),
        ]}
      />

      <div className="p-8 space-y-7">
        {/* Ressalva dos totais, e não do mix por modelo.
            O consumo de embedding não está em número nenhum desta tela: o
            `embedding_service` do indexing não grava custo nem token, e o
            `by_model` sai só de `agent_executions`/`chat_messages`. Quem
            conferir contra a fatura da AWS vai achar diferença, e o lugar de
            dizer isso é ao lado dos totais.
            Nomear o modelo dentro do card "Mix de tokens por modelo" — onde
            ele valeria 0% — sugeria medição onde não há; foi o que esta linha
            veio substituir. */}
        {embeddingModel && (
          <p
            data-testid="embedding-caveat"
            className="font-mono text-[10px] tracking-[0.04em] text-[#7b8799]"
          >
            {t(
              "Totals cover agent and chat model calls. Embedding usage ({{model}}) is not recorded anywhere, so it is not counted here.",
              { model: embeddingModel },
            )}
          </p>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card>
            <CardHead overline={t("BY AGENT")} title={t("Spend by agent")} />
            {byAgent.length === 0 ? (
              <EmptyCard text={t("No cost data yet. Run agents or chat to generate usage.")} />
            ) : (
              <div className="px-5 py-4">
                {byAgent.map((a) => {
                  const widthPct = costs.total_cost > 0 ? (a.total_cost / costs.total_cost) * 100 : 0;
                  const runs = a.execution_count + a.chat_count;
                  const name = AGENT_DISPLAY_NAMES[a.agent_type] || a.agent_type;
                  // Um só texto para hover (`title`) e leitor de tela
                  // (`aria-label`): eram duas cópias literais da mesma string.
                  // A quebra execução/chat vinha na gaveta que a #429
                  // aposentou (`CostSummaryTab`), e o payload sempre a
                  // trouxe. Ela volta aqui, e não como coluna: a linha já
                  // tem quatro e mais duas apertariam o nome do agente.
                  // Quem precisa do número exato tem o CSV.
                  const label = [
                    `${name} — ${detailedUsd(a.total_cost)}`,
                    `${detailedTokens(a.total_tokens)} ${t("tokens")}`,
                    tp(t, runs, "1 run", "{{n}} runs"),
                    // Dois `tp` em vez de uma chave composta: singular e
                    // plural de cada metade. E "mensagem de chat", não
                    // "chat": `chat_count` conta mensagens (`COUNT(*)` sobre
                    // `chat_messages`), não conversas.
                    `${tp(t, a.execution_count, "1 execution", "{{n}} executions")} · ${tp(
                      t,
                      a.chat_count,
                      "1 chat message",
                      "{{n}} chat messages",
                    )}`,
                  ].join(", ");
                  return (
                    <div
                      key={a.agent_type}
                      className="grid grid-cols-[140px_1fr_110px_90px] gap-3.5 items-center py-2.5 border-b border-[#f2f4f7] last:border-b-0 text-[13px] text-[#08254c]"
                    >
                      <div className="font-semibold truncate">{name}</div>
                      <div
                        className="h-3.5 bg-[#f7f8fb] rounded overflow-hidden relative"
                        title={label}
                        tabIndex={0}
                        aria-label={label}
                      >
                        <div
                          className="h-full rounded"
                          style={{ width: `${widthPct}%`, background: CHART_BLUE }}
                        />
                      </div>
                      <div className="font-mono text-[#596b85] text-[11px] text-right">
                        {detailedTokens(a.total_tokens)}
                      </div>
                      {/* `detailedUsd`, não `compactUsd`: esta é a superfície de
                          drilldown. Com centavos arredondados, dois agentes de
                          custo diferente abaixo de $0.01 viravam a mesma célula
                          `$0.00` — o defeito que a PR #788 já tinha corrigido do
                          lado dos tokens. */}
                      <div className="font-mono text-[#08254c] font-semibold text-right">
                        {detailedUsd(a.total_cost)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card>
            <div data-testid="by-model">
            <CardHead
              overline={t("BY MODEL")}
              title={t("Token mix by model")}
              right={
                byModel.length > 0 ? (
                  <Legend chips={modelLegendChips(byModel, t)} />
                ) : undefined
              }
            />
            {byModel.length === 0 ? (
              <EmptyCard text={t("No model measured yet. Run agents or chat to generate usage.")} />
            ) : (
              <div className="px-5 py-5">
                <ModelStack models={byModel} t={t} />
              </div>
            )}
            </div>
          </Card>
        </section>

        <section>
          <SectionHead
            overline={t("DAILY TREND")}
            title={t("Token usage over time")}
            right={
              byDay.length > 0 ? (
                <Legend
                  chips={[
                    { label: t("Input").toUpperCase(), color: CHART_BLUE },
                    { label: t("Output").toUpperCase(), color: MODEL_SLOTS[1] },
                    ...(byDay.some((d) => unrecordedTokens(d) > 0)
                      ? [{ label: t("Split not recorded").toUpperCase(), color: MODEL_OTHER }]
                      : []),
                  ]}
                />
              ) : undefined
            }
          />
          <Card>
            <div className="px-6 py-5">
              {byDay.length === 0 ? (
                <EmptyCard text={t("No usage history yet. The chart fills in as agents and chats run.")} />
              ) : (
                <>
                  <DailyChart days={byDay} t={t} />
                  <SplitCoverage days={byDay} t={t} />
                </>
              )}
            </div>
          </Card>
        </section>

        <section>
          <SectionHead
            overline={t("INVOICE LINES")}
            title={t("Recent charges")}
            sub={t("The most recent runs that actually cost something.")}
          />
          <Card>
            <InvoiceLines
              runs={charges}
              loading={runsLoading}
              error={runsError}
              onRetry={() => refetchRuns()}
              t={t}
            />
          </Card>
        </section>

        <Mantra>
          {t("EVERY TOKEN")} · <MantraAccent>{t("MEASURED")}</MantraAccent> · {t("ATTRIBUTED")}
        </Mantra>
      </div>
    </div>
  );
}

/**
 * Ressalva de cobertura do split, quando ela existe.
 *
 * Só aparece se algum dia da série está medido pela metade. Série inteiramente
 * medida não ganha nota nenhuma — ressalva pendurada onde não há ressalva
 * ensina o leitor a ignorá-la, e aí ela some quando importa.
 */
function SplitCoverage({ days, t }: { days: CostByDay[]; t: TFn }) {
  if (!days.some(isPartial)) return null;
  const measured = days.reduce((sum, d) => sum + (d.measured_entries ?? 0), 0);
  const total = days.reduce((sum, d) => sum + (d.total_entries ?? 0), 0);

  return (
    <p
      data-testid="split-coverage"
      className="mt-3 font-mono text-[10px] tracking-[0.04em] text-[#7b8799]"
    >
      {t(
        "Input/output split recorded for {{measured}} of {{total}} entries. Each bar is the day's total; the gray part is the tokens whose split was not recorded.",
        { measured, total },
      )}
    </p>
  );
}

/**
 * Linhas de fatura — as execuções recentes que cobraram.
 *
 * Volta com seis das sete colunas do mock original. A sétima, `Cache`
 * ("58% hit"), fica de fora: taxa de acerto de cache nunca foi medida em
 * lugar nenhum da plataforma, e era o único campo do card sem origem. As
 * outras seis passaram a existir com esta issue — a coluna in/out é o
 * `prompt_tokens`/`completion_tokens` que o executor só começou a gravar
 * agora, e as demais vêm da rota `/runs`.
 */
function InvoiceLines({
  runs,
  loading,
  error,
  onRetry,
  t,
}: {
  runs: ProjectRun[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  t: TFn;
}) {
  // O guarda de topo da tela é o `costsLoading`, de **outra** consulta. Sem
  // este ramo, quando o resumo de custo respondia primeiro, `runs` ainda era
  // `[]` e o card dizia "nenhuma cobrança gravada" ao lado de um hero que já
  // mostrava gasto. Mesmo ramo do `ProjectRuns.tsx`.
  if (loading) {
    return (
      <div data-testid="invoice-lines">
        <div data-testid="invoice-lines-loading" className="p-5 space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  // O card lê `/runs`, consulta separada da que desenha o resto da tela, e ela
  // pode falhar sozinha. Sem este ramo, a falha virava lista vazia e o card
  // dizia "nenhuma cobrança gravada ainda" ao lado de um hero com gasto.
  if (error) {
    return (
      <div data-testid="invoice-lines">
        <ErrorState message={t("Could not load the recent charges.")} onRetry={onRetry} />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div data-testid="invoice-lines">
        <EmptyCard text={t("No charge recorded yet. Costs appear as agents and chats run.")} />
      </div>
    );
  }

  return (
    <div data-testid="invoice-lines">
      <table className="tbl">
        <thead>
          <tr>
            <th>{t("Date")}</th>
            <th>{t("Run")}</th>
            <th>{t("Agent")}</th>
            <th>{t("Model")}</th>
            <th>{t("Tokens (in / out)")}</th>
            <th style={{ textAlign: "right" }}>{t("Cost")}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const split = measuredSplit(run.metrics ?? {});
            return (
              <tr key={run.id}>
                <td className="mono">{formatDateTime(run.started_at)}</td>
                <td className="mono" title={run.id}>
                  #{run.id.slice(0, 5).toUpperCase()}
                </td>
                <td>{AGENT_DISPLAY_NAMES[run.agent_type] || run.agent_type}</td>
                <td className="mono muted">{runModelLabel(run.metrics ?? null) ?? "—"}</td>
                {/* Sem os dois lados gravados, travessão — e não o total
                    dividido no olho, nem `0 / 0`. */}
                <td className="mono">
                  {split
                    ? `${detailedTokens(split.prompt)} / ${detailedTokens(split.completion)}`
                    : "—"}
                </td>
                <td className="mono" style={{ textAlign: "right" }}>
                  {/* Sem `!`: o `has_cost` garante custo na resposta, mas a
                      célula não deve depender de um filtro que mora em outro
                      serviço para não quebrar. */}
                  {run.metrics?.cost_estimate != null
                    ? detailedUsd(run.metrics.cost_estimate)
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 px-5 text-center">
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

interface ModelSlot {
  key: string;
  label: string;
  color: string;
  total_tokens: number;
  total_cost: number;
}

/** Rótulo + cor de cada modelo, na mesma ordem que o backend já devolve
 * (custo decrescente). Do 4º em diante, todos somam numa fatia "Other" só
 * (cinza, não um hue novo: a skill dataviz não permite gerar uma 9ª cor
 * categórica). Uma fatia por modelo excedente partia "o resto" em pedaços
 * com o mesmo rótulo e a mesma cor. */
function modelSlots(models: CostByModel[], t: TFn): ModelSlot[] {
  const slots: ModelSlot[] = models.slice(0, MODEL_SLOTS.length).map((m, i) => ({
    key: m.model,
    label: m.model_label || m.model,
    color: MODEL_SLOTS[i],
    total_tokens: m.total_tokens,
    total_cost: m.total_cost,
  }));
  const rest = models.slice(MODEL_SLOTS.length);
  if (rest.length > 0) {
    slots.push({
      key: "other",
      label: t("Other"),
      color: MODEL_OTHER,
      total_tokens: rest.reduce((sum, m) => sum + m.total_tokens, 0),
      total_cost: rest.reduce((sum, m) => sum + m.total_cost, 0),
    });
  }
  return slots;
}

function modelLegendChips(models: CostByModel[], t: TFn) {
  // `totalOf` fora do map: dentro dele o denominador era recalculado por
  // chip, varrendo a lista inteira a cada volta.
  const total = totalOf(models);
  return modelSlots(models, t).map(({ total_tokens, color, label }) => ({
    label: `${label} · ${total_tokens > 0 ? Math.round((total_tokens / total) * 100) : 0}%`,
    color,
  }));
}

function totalOf(models: CostByModel[]): number {
  return models.reduce((sum, m) => sum + m.total_tokens, 0) || 1;
}

/** Barra única de composição (part-to-whole): cada modelo é um segmento,
 * proporcional à participação em tokens medidos. Segmento estreito não
 * ganha rótulo interno (o texto seria cortado) — a `Tooltip` carrega o valor
 * exato, e a legenda acima carrega o nome. */
function ModelStack({ models, t }: { models: CostByModel[]; t: TFn }) {
  const total = totalOf(models);
  return (
    <div className="h-8 rounded-md overflow-hidden flex gap-0.5">
      {modelSlots(models, t).map(({ key, color, label, total_tokens, total_cost }) => {
        const pct = (total_tokens / total) * 100;
        const fits = pct >= 12;
        return (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <div
                className="h-full flex items-center justify-center overflow-hidden"
                style={{ flex: `${Math.max(pct, 0.5)} 0 0%`, background: color }}
                tabIndex={0}
                aria-label={`${label}: ${detailedTokens(total_tokens)} ${t("tokens")}, ${detailedUsd(total_cost)}, ${Math.round(pct)}%`}
              >
                {fits && (
                  <span className="font-mono text-[10px] font-semibold text-white px-1.5 truncate">
                    {Math.round(pct)}%
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="font-semibold">{label}</div>
              <div className="font-mono text-[11px]">
                {detailedTokens(total_tokens)} {t("tokens")} · {detailedUsd(total_cost)}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Largura real do container, para o `viewBox` bater 1:1 com o pixel.
 *
 * Sem isto o SVG dependia de `preserveAspectRatio="none"` para preencher a
 * largura — o que estica **só** o eixo X: círculo vira elipse e a espessura
 * da linha fica anisotrópica (quanto mais estreito o container, mais óbvio).
 * Com o `viewBox` na largura medida, a escala é 1 nos dois eixos e nada
 * distorce.
 *
 * 🔶 jsdom não faz layout (`getBoundingClientRect` devolve 0) e não tem
 * `ResizeObserver`; o fallback de largura mantém o gráfico renderizável em
 * teste, que é onde a geometria não pode ser verificada de qualquer forma.
 */
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(element.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/** Largura suposta antes da primeira medição (e em jsdom). */
const CHART_FALLBACK_WIDTH = 760;

/** O split, quando as **duas** metades foram medidas — de um dia da série ou
 *  de uma execução, que gravam as mesmas duas chaves. Meia metade não serve: a
 *  outra viraria implicitamente `total − medido`, que é palpite. */
function measuredSplit(m: {
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
}): { prompt: number; completion: number } | null {
  if (m.prompt_tokens == null || m.completion_tokens == null) return null;
  return { prompt: m.prompt_tokens, completion: m.completion_tokens };
}

/** O dia foi medido pela metade: parte das entradas gravou o split, parte não. */
function isPartial(d: CostByDay): boolean {
  if (d.measured_entries == null || d.total_entries == null) return false;
  return d.measured_entries < d.total_entries;
}



/** Tokens do dia cujo split ninguém gravou: o total menos o que foi medido.
 *
 *  Nunca negativo. Um dia em que alguma entrada gravou o split mas não o
 *  total (`COALESCE` zera o total, o `SUM` do split não) daria diferença
 *  abaixo de zero, e uma barra de altura negativa desenha para fora do
 *  gráfico. */
function unrecordedTokens(d: CostByDay): number {
  const split = measuredSplit(d);
  if (!split) return d.total_tokens;
  return Math.max(0, d.total_tokens - split.prompt - split.completion);
}

/**
 * Altura da pilha de um dia: o maior entre o total e o split medido.
 *
 * Normalmente é o total, e o cinza cobre a diferença. Mas quando uma entrada
 * grava o split e não o total — o `COALESCE` do backend zera um e o `SUM` do
 * outro não —, o split passa do total, e ali ele é a medição mais completa: o
 * dia consumiu **pelo menos** isso. Encolher as barras para caber no total
 * seria descartar dado medido; ignorar a diferença na escala fazia a pilha
 * sair pelo topo do gráfico e distorcer todos os outros dias.
 */
function stackTokens(d: CostByDay): number {
  const split = measuredSplit(d);
  return split ? Math.max(d.total_tokens, split.prompt + split.completion) : d.total_tokens;
}

/**
 * Tendência diária — barras empilhadas de entrada/saída por dia.
 *
 * Esta era a forma do mock (`DAILY`, pares `{in, out}`), e ela só pôde voltar
 * porque o #867 criou a fonte: o executor passou a gravar
 * `prompt_tokens`/`completion_tokens` e o `get_cost_summary` a somá-los por
 * dia, com `measured_entries`/`total_entries` ao lado.
 *
 * **O terceiro segmento é o que torna a pilha honesta.** O split cobre só as
 * entradas que o gravaram, então `entrada + saída` costuma ser menor que
 * `total_tokens`. Empilhar só os dois deixaria a barra mais baixa que o total
 * do dia sem dizer por quê — e comparada entre dias, a altura passaria a
 * significar "quanto foi medido", não "quanto foi gasto". A diferença entra
 * como segmento cinza nomeado ("split não gravado"): a pilha fecha com o
 * total, a altura volta a ser o consumo, e a parte desconhecida se identifica
 * em vez de sumir.
 *
 * Três cores, então há legenda — ao contrário da série única de antes, a
 * identidade de cada segmento não vem do eixo.
 */
function DailyChart({ days, t }: { days: CostByDay[]; t: TFn }) {
  const { ref, width: measured } = useMeasuredWidth();
  const width = measured > 0 ? measured : CHART_FALLBACK_WIDTH;
  const height = 140;
  const padY = 16;
  const plotHeight = height - padY * 2;
  // Pela altura da pilha, não pelo total: é a pilha que se desenha.
  const maxTokens = Math.max(...days.map(stackTokens), 1);
  // Largura da faixa por dia, com a barra ocupando 60% dela — o resto é o
  // respiro entre barras.
  const slot = width / Math.max(days.length, 1);
  const barWidth = Math.max(2, slot * 0.6);

  return (
    <div ref={ref} className="w-full">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        {days.map((d, i) => {
          const split = measuredSplit(d);
          const unrecorded = unrecordedTokens(d);
          const x = i * slot + (slot - barWidth) / 2;
          const scale = (value: number) => (value / maxTokens) * plotHeight;

          // De baixo para cima: entrada, saída, não gravado. A ordem importa —
          // o que é medido fica na base, e a incerteza no topo, onde ela não
          // empurra visualmente as duas partes conhecidas.
          const segments: { key: string; value: number; fill: string }[] = [];
          if (split) {
            segments.push({ key: "in", value: split.prompt, fill: CHART_BLUE });
            segments.push({ key: "out", value: split.completion, fill: MODEL_SLOTS[1] });
          }
          if (unrecorded > 0) {
            segments.push({ key: "unrecorded", value: unrecorded, fill: MODEL_OTHER });
          }

          let cursor = height - padY;
          const label = [
            `${formatDayShort(d.day)}: ${detailedTokens(d.total_tokens)} ${t("tokens")}, ${detailedUsd(d.total_cost)}`,
            split
              ? `${t("Input")} ${detailedTokens(split.prompt)} / ${t("Output")} ${detailedTokens(split.completion)}`
              : null,
            // O segmento cinza diz quantos TOKENS ficaram sem split; este diz
            // quantas EXECUÇÕES. São perguntas diferentes — cinco runs
            // pequenas sem split pintam pouca barra e ainda assim são cinco
            // cobranças sem detalhe.
            isPartial(d)
              ? t("from {{measured}} of {{total}} entries", {
                  measured: d.measured_entries,
                  total: d.total_entries,
                })
              : null,
            unrecorded > 0
              ? `${t("Split not recorded")} ${detailedTokens(unrecorded)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <g
              key={d.day}
              data-testid={`daily-bar-${d.day}`}
              tabIndex={0}
              role="img"
              aria-label={label}
            >
              <title>{label}</title>
              {segments.map((segment) => {
                const barHeight = scale(segment.value);
                cursor -= barHeight;
                return (
                  <rect
                    key={segment.key}
                    x={x}
                    y={cursor}
                    width={barWidth}
                    height={barHeight}
                    fill={segment.fill}
                  />
                );
              })}
              {i === 0 || i === days.length - 1 ? (
                <text
                  x={x + barWidth / 2}
                  y={height - 2}
                  textAnchor={i === 0 ? "start" : "end"}
                  fontFamily="ui-monospace,monospace"
                  fontSize="9"
                  fill="#7b8799"
                >
                  {formatDayShort(d.day)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
